import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { palette, status } from '../../../../style-utils/theme';
import { countOf } from '../../../../function-utils/plural';
import { BackBar, BackButton } from '../../../layout/back-button';
import { Text } from '../../../../style-utils/text';
import {
  Collapse,
  InlineMessage,
  Pop,
  Presence,
  Shake,
  Staleable,
  StateSwitch,
  listItemMotion,
} from '../../../../motion';
import {
  ListSkeleton,
  skeletonHeight,
} from '../dashboard-shared-components/load-state';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import {
  AvatarFallbackLarge,
  AvatarLarge,
  BannerIcon,
  CapabilityTag,
  Column,
  CountPill,
  DashboardContent,
  DetailHeader,
  DetailIdentity,
  DetailStats,
  EmptyState,
  EmptyStateIcon,
  ExplainerArrow,
  ExplainerCard,
  ExplainerHalf,
  GrantCard,
  GrantList,
  GrantMeta,
  GrantTarget,
  GrantTop,
  InlineError,
  InlineSuccess,
  OrgWideBanner,
  PrimaryButton,
  ResolvedIdentity,
  ResolvedList,
  ResolvedRow,
  ResolvedVia,
  RevokeButton,
  RolePill,
  ScopeTypePill,
  Section,
  SectionEmpty,
  SectionEyebrow,
  SectionHeader,
  SectionTitleGroup,
  TagRow,
  ViaLabel,
  ViaTag,
  WarningBanner,
  WarningIcon,
} from './dashboard-scopes.styled';
import { DashboardAddGrant } from './dashboard-add-grant';
import {
  CAPABILITY_COPY,
  GRANT_REASON_LABELS,
  ORG_WIDE_EXPLANATIONS,
  ORG_WIDE_LABELS,
  SCOPE_TYPE_LABELS,
  buildingsFromGrant,
  displayName,
  explainBuildingAccess,
  getInitials,
  grantTargetLabel,
  isFullyRedundant,
  isSelfExpandingScope,
  orgWideTone,
  scopeTypeTone,
} from './scope-vocabulary';
import {
  AccessGrant,
  AccessGrantCreated,
  StaffScopes,
} from '../../../../interfaces/hierarchy-interfaces';
import {
  HierarchyApiError,
  getStaffScopes,
  revokeStaffScope,
} from '../../../../api/hierarchy-client';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  ROLES,
  normalizeRole,
  outranks,
  roleLabel,
} from '../../../../auth/roles';
import {
  dateIn,
  deviceZone,
  formatDayKey,
  isDayKey,
} from '../../../../function-utils/building-time';

interface DashboardStaffScopeDetailProps {
  // Supplied when the screen is rendered inline from the summary table. Left
  // out when it is mounted on its own route, where the id comes from the URL.
  userId?: string;
  onBack?: () => void;
}

/* A grant's date. When the API sends a bare date ('2026-10-07') it is a
   calendar day and is printed as one: through `new Date()` it is read as UTC
   midnight and prints the day BEFORE for anyone west of Greenwich. A full
   instant (a created-at) is a moment, and is shown on the reader's own clock —
   an account-level fact that belongs to no building (R-T6). */
const formatDate = (value: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  if (isDayKey(value)) return formatDayKey(value, options);

  return dateIn(value, deviceZone(), options) || value;
};

export const DashboardStaffScopeDetail: React.FC<
  DashboardStaffScopeDetailProps
> = ({ userId: userIdProp, onBack }) => {
  const params = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const userId = userIdProp || params.userId || '';

  const { user, permissions } = useAuth();
  const holdsScopeManage = can(permissions, PERMISSIONS.SCOPE_MANAGE);

  const [data, setData] = useState<StaffScopes | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingGrant, setIsAddingGrant] = useState<boolean>(false);
  const [grantToRevoke, setGrantToRevoke] = useState<AccessGrant | null>(null);
  /* Kept apart from the page's `error`: that one means the SCREEN could not
     load, and rendering a revoke failure there put it under the backdrop. */
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /* Counts failed revokes, so the error line shakes again on a second failure
     instead of sitting still. */
  const [revokeAttempt, setRevokeAttempt] = useState(0);

  const load = useCallback(async () => {
    if (!userId) {
      setError('No staff member selected.');
      setLoading(false);

      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getStaffScopes(userId);

      setData(response);
    } catch (loadError) {
      setError(
        loadError instanceof HierarchyApiError
          ? loadError.message
          : 'Could not load this staff member’s access.'
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // AN EXPLICIT DESTINATION, not navigate(-1). This screen is reachable by
  // deep link and by refresh, and in both cases there is no in-app history
  // entry — the offset then either does nothing (a dead button on the one
  // control that gets somebody unstuck) or walks them out of the application.
  // `onBack` is still honoured because a host that embeds this detail wants to
  // close its own panel rather than change route.
  const handleBack = () => {
    if (onBack) {
      onBack();

      return;
    }

    navigate('/dashboard/staff-access', { replace: false });
  };

  // The create response reports how many buildings the grant actually resolved
  // to, which is the number worth echoing back — a portfolio grant that turns
  // into twelve buildings is the moment to catch an over-broad assignment.
  const handleGranted = async (created: AccessGrantCreated) => {
    setIsAddingGrant(false);
    setNotice(
      created.resolved_building_count === 1
        ? 'Grant added. It covers 1 building.'
        : `Grant added. It covers ${created.resolved_building_count} buildings.`
    );
    await load();
  };

  // The shared ConfirmDialog guards the double-press and shows the pending
  // state on its own button; this only has to do the work and report.
  const handleConfirmRevoke = async () => {
    if (!grantToRevoke) {
      return;
    }

    setRevokeError(null);

    try {
      await revokeStaffScope(grantToRevoke.id);
      setGrantToRevoke(null);
      setNotice('Grant revoked. Access was updated immediately.');
      await load();
    } catch (caught) {
      /* THE FAILURE, INSIDE THE DIALOG THAT CAUSED IT. A failed revoke leaves
         `grantToRevoke` set, so the dialog stays open — and the reason joins
         its message (see revokeMessage below) rather than landing on the page
         body underneath the scrim, where nobody pressing Revoke could see it. */
      setRevokeError(
        caught instanceof HierarchyApiError
          ? caught.message
          : 'Could not revoke the grant.'
      );
      setRevokeAttempt(count => count + 1);
    }
  };

  /* THREE STATES, ONE BOX. The spinner-and-sentence is a skeleton shaped like
     the cards that follow; StateSwitch crossfades skeleton, failure and detail
     inside the same container. A reload over data already held (after a grant
     is added or revoked) is a refresh: the detail stays and dims. */
  const stateKey = loading && !data ? 'loading' : !data ? 'error' : 'ready';
  let body: React.ReactNode;
  let revokeMessage = '';

  if (stateKey === 'loading') {
    body = <ListSkeleton rows={4} rowHeight={88} label="access" />;
  } else if (!data) {
    body = (
      <Column>
        <BackBar>
          <BackButton
            to="/dashboard/staff-access"
            label="Back to staff access"
            onClick={handleBack}
          />
        </BackBar>
        <EmptyState>
          <EmptyStateIcon>&#9888;</EmptyStateIcon>
          <Text fontSize={18} fontWeight={700} mb={0} color={palette.ink}>
            Could not load this staff member
          </Text>
          <Text fontSize={14} mt={0} mb={0} color={palette.slate}>
            {error || 'They may no longer exist in your organisation.'}
          </Text>
        </EmptyState>
      </Column>
    );
  } else {
    body = renderDetail(data);
  }

  return (
    <DashboardContent id="dashboard-staff-scope-detail">
      <StateSwitch state={stateKey} minHeight={skeletonHeight(4, 88)}>
        {body}
      </StateSwitch>

      {/* THE SHARED CONFIRMATION, which animates in and out and holds the
          focus. The impact is stated BEFORE it happens, in numbers, and a
          failed attempt's reason is appended so it is read inside the dialog
          that caused it. */}
      <ConfirmDialog
        open={Boolean(grantToRevoke)}
        title="Revoke this grant?"
        message={revokeMessage}
        primaryLabel="Revoke grant"
        primaryVariant="destructive"
        dismissLabel="Cancel"
        onPrimary={handleConfirmRevoke}
        onDismiss={() => {
          setGrantToRevoke(null);
          setRevokeError(null);
        }}
      />
    </DashboardContent>
  );

  function renderDetail(loaded: StaffScopes): React.ReactNode {
  const { user: staff, grants, resolved_buildings: resolvedBuildings } = loaded;
  const staffLabel = displayName(staff.name, staff.email);

  // ── THE SERVER'S RULE, MIRRORED SO THE PAGE DOES NOT OFFER A DEAD END ─────
  // `scope:manage` says WHETHER this person manages access at all; it does not
  // say WHOSE. assertMayScope in lobbero_be/src/CRUD/staff-scopes.js decides
  // that per target: never yourself, and only somebody STRICTLY BELOW your own
  // role — which makes an Owner unreachable to everyone, including another
  // Owner.
  //
  // This screen used to gate Add grant and Revoke on the permission alone, so a
  // property manager opening an Owner's access page was shown both controls and
  // got a 403 from each. The users list has mirrored the same rule per row
  // since it was written (dashboard-users.tsx); this is the surface that was
  // missed.
  //
  // MIRRORED, NOT ENFORCED HERE. The server refuses regardless.
  const actorRole = normalizeRole(user?.role);
  const canManageScope =
    holdsScopeManage &&
    String(staff.id) !== String(user?.id ?? '') &&
    normalizeRole(staff.role) !== ROLES.OWNER &&
    outranks(actorRole, normalizeRole(staff.role));
  const openGrants = grants.filter(grant => grant.is_open);
  const orgWideGrant = openGrants.find(
    grant => grant.scope_type === 'organization'
  );
  // WP-24. The closure being empty is only "reaches nothing" when they do not
  // reach everything WITHOUT a grant. Server-computed, not re-derived here:
  // `orgWideGrant` above catches one of the three cases and neither of the
  // other two — an Owner (a role short-circuit in middleware/scope.js, so no
  // grant row ever exists) and an organisation with scope enforcement off,
  // where every staff member reaches every building.
  const orgWideReason = staff.org_wide ? staff.org_wide_reason : null;
  const hasNoAccess = resolvedBuildings.length === 0 && !orgWideReason;

  if (grantToRevoke) {
    const impact = isFullyRedundant(grantToRevoke, grants, resolvedBuildings)
      ? 'Another grant covers the same buildings, so this will not change what they can reach.'
      : `${staffLabel} loses access to ${countOf(
          buildingsFromGrant(grantToRevoke, resolvedBuildings),
          'building'
        )} immediately.`;

    revokeMessage = `${SCOPE_TYPE_LABELS[grantToRevoke.scope_type]} · ${grantTargetLabel(
      grantToRevoke
    )}. ${impact}${revokeError ? ` ${revokeError}` : ''}`;
  }

  return (
    <Staleable stale={loading}>
      <Column>
        <BackBar>
          <BackButton
            to="/dashboard/staff-access"
            label="Back to staff access"
            onClick={handleBack}
          />
        </BackBar>

        <DetailHeader>
          <AvatarLarge>
            <AvatarFallbackLarge>
              {getInitials(staff.name || staff.email)}
            </AvatarFallbackLarge>
          </AvatarLarge>

          <DetailIdentity>
            <Text
              mt={0}
              mb={0}
              fontSize={20}
              fontWeight={700}
              color={palette.ink}
            >
              {staffLabel}
            </Text>
            <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
              {staff.email || 'No email on file'}
            </Text>
          </DetailIdentity>

          <DetailStats>
            <RolePill isOwner={normalizeRole(staff.role) === ROLES.OWNER}>
              {roleLabel(staff.role)}
            </RolePill>
            <CountPill tone="neutral">
              {openGrants.length} active{' '}
              {openGrants.length === 1 ? 'grant' : 'grants'}
            </CountPill>
            <CountPill tone={hasNoAccess ? 'warning' : 'success'}>
              {resolvedBuildings.length}{' '}
              {resolvedBuildings.length === 1 ? 'building' : 'buildings'}
            </CountPill>
          </DetailStats>
        </DetailHeader>

        {/* The confirmation LANDS (Pop) and the refusal SHAKES, and both
            unfold their space (InlineMessage) so the explainer below slides
            rather than jumps. */}
        <InlineMessage
          role="status"
          message={
            notice ? (
              <div style={{ marginTop: '16px' }}>
                <Pop as="div">
                  <InlineSuccess>
                    <span aria-hidden="true">&#10003;</span>
                    <span>{notice}</span>
                  </InlineSuccess>
                </Pop>
              </div>
            ) : undefined
          }
        />

        <InlineMessage
          role="alert"
          message={
            error ? (
              <div style={{ marginTop: '16px' }}>
                <Shake active attempt={revokeAttempt}>
                  <InlineError>
                    <span aria-hidden="true">&#9888;</span>
                    <span>{error}</span>
                  </InlineError>
                </Shake>
              </div>
            ) : undefined
          }
        />

        {/* The mental model, stated once at the top. Everything below is one of
            these two halves, and conflating them is the mistake this screen is
            built to prevent. */}
        <ExplainerCard>
          <ExplainerHalf>
            <SectionEyebrow>Grants — what you assigned</SectionEyebrow>
            <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
              Your intent, written down. You add and revoke these directly.
            </Text>
          </ExplainerHalf>

          <ExplainerArrow aria-hidden="true">&#8594;</ExplainerArrow>

          <ExplainerHalf>
            <SectionEyebrow>
              Resolved access — what they can reach
            </SectionEyebrow>
            <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
              Worked out from the grants. One grant can expand into many
              buildings, and this list is recomputed whenever a grant changes.
            </Text>
          </ExplainerHalf>
        </ExplainerCard>

        {/* Reaches every building without a grant saying so. Stated here
            because the resolved list below is legitimately EMPTY for an owner,
            and an empty list with no explanation reads as "no access". */}
        {orgWideReason &&
          (() => {
            // Enforcement being off is a SETTING that silently widens everybody
            // in the organisation, so it borrows the warning treatment. Owner
            // and an organisation grant are working as designed and use the
            // same iris callout the "every building added later" promise uses.
            const Banner =
              orgWideTone(orgWideReason) === 'warning'
                ? WarningBanner
                : OrgWideBanner;
            const Icon =
              orgWideTone(orgWideReason) === 'warning'
                ? WarningIcon
                : BannerIcon;

            return (
              <div style={{ marginTop: '16px' }}>
                <Banner>
                  <Icon aria-hidden="true">&#9432;</Icon>
                  <div>
                    <Text
                      mt={0}
                      mb={0}
                      fontSize={14}
                      fontWeight={600}
                      color={palette.ink}
                    >
                      {ORG_WIDE_LABELS[orgWideReason]}
                    </Text>
                    <Text mt={4} mb={0} fontSize={14} color={palette.slate}>
                      {ORG_WIDE_EXPLANATIONS[orgWideReason]}
                    </Text>
                  </div>
                </Banner>
              </div>
            );
          })()}

        {/* An active staff member on zero buildings signs in and sees nothing.
            That is invisible from every other screen. */}
        {hasNoAccess && (
          <div style={{ marginTop: '16px' }}>
            <WarningBanner>
              <WarningIcon>&#9888;</WarningIcon>
              <div>
                <Text
                  mt={0}
                  mb={0}
                  fontSize={14}
                  fontWeight={600}
                  color={status.warning.fg}
                >
                  This person cannot reach any building
                </Text>
                <Text mt={4} mb={0} fontSize={14} color={palette.slate}>
                  {staffLabel} can sign in, but every building list will be
                  empty until a grant is added below.
                </Text>
              </div>
            </WarningBanner>
          </div>
        )}

        {/* ── Grants ─────────────────────────────────────────────────── */}
        <Section>
          <SectionHeader>
            <SectionTitleGroup>
              <SectionEyebrow>Step 1 — intent</SectionEyebrow>
              <Text
                mt={0}
                mb={0}
                fontSize={18}
                fontWeight={700}
                color={palette.ink}
              >
                Grants
              </Text>
              <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
                What was explicitly assigned to {staffLabel}.
              </Text>
            </SectionTitleGroup>

            {canManageScope && !isAddingGrant && (
              <PrimaryButton
                type="button"
                onClick={() => setIsAddingGrant(true)}
              >
                Add grant
              </PrimaryButton>
            )}
          </SectionHeader>

          {/* The form UNFOLDS under the heading and folds away on cancel or
              on success, so the grant list slides rather than jumps. */}
          <Collapse open={isAddingGrant && canManageScope}>
            <div style={{ paddingBottom: '8px' }}>
              <DashboardAddGrant
                userId={userId}
                staffName={staffLabel}
                onGranted={handleGranted}
                onCancel={() => setIsAddingGrant(false)}
              />
            </div>
          </Collapse>

          {grants.length === 0 ? (
            <SectionEmpty>
              <Text
                mt={0}
                mb={0}
                fontSize={14}
                fontWeight={600}
                color={palette.inkSoft}
              >
                No grants yet
              </Text>
              <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
                {canManageScope
                  ? 'Add one to decide which part of the business they govern.'
                  : 'An owner has not assigned this person any scope.'}
              </Text>
            </SectionEmpty>
          ) : (
            <GrantList>
              {/* Cards arrive in a cascade, slide when a sibling leaves, and
                  animate out inside the Presence. */}
              <Presence>
              {grants.map((grant, index) => {
                const coveredBuildings = buildingsFromGrant(
                  grant,
                  resolvedBuildings
                );
                const redundant = isFullyRedundant(
                  grant,
                  grants,
                  resolvedBuildings
                );

                return (
                  <GrantCard
                    key={grant.id}
                    {...listItemMotion(index)}
                    whileHover={{ y: -1 }}
                    isClosed={!grant.is_open}
                  >
                    <GrantTop>
                      <GrantTarget>
                        <ScopeTypePill tone={scopeTypeTone(grant.scope_type)}>
                          {SCOPE_TYPE_LABELS[grant.scope_type]}
                        </ScopeTypePill>
                        <Text
                          mt={0}
                          mb={0}
                          fontSize={16}
                          fontWeight={600}
                          color={palette.ink}
                        >
                          {grantTargetLabel(grant)}
                        </Text>
                        {!grant.is_open && (
                          <CountPill tone="neutral">Inactive</CountPill>
                        )}
                      </GrantTarget>

                      {canManageScope && (
                        <RevokeButton
                          type="button"
                          onClick={() => setGrantToRevoke(grant)}
                        >
                          Revoke
                        </RevokeButton>
                      )}
                    </GrantTop>

                    <TagRow>
                      <CapabilityTag
                        tone="success"
                        muted={!grant.can_operate}
                        title={CAPABILITY_COPY.operate.help}
                      >
                        {grant.can_operate ? '✓ ' : ''}
                        {CAPABILITY_COPY.operate.title}
                      </CapabilityTag>
                      <CapabilityTag
                        tone="warning"
                        muted={!grant.can_administer}
                        title={CAPABILITY_COPY.administer.help}
                      >
                        {grant.can_administer ? '✓ ' : ''}
                        {CAPABILITY_COPY.administer.title}
                      </CapabilityTag>
                      <CountPill tone="neutral">
                        {GRANT_REASON_LABELS[grant.reason]}
                      </CountPill>
                    </TagRow>

                    <GrantMeta>
                      <Text mt={0} mb={0} fontSize={12} color={palette.slate}>
                        {grant.is_open
                          ? `Expands to ${coveredBuildings} ${
                              coveredBuildings === 1 ? 'building' : 'buildings'
                            }`
                          : 'Expired — expands to nothing'}
                      </Text>
                      <Text
                        mt={0}
                        mb={0}
                        fontSize={12}
                        color={palette.slateLight}
                      >
                        Since {formatDate(grant.valid_from)}
                        {grant.valid_until
                          ? ` · until ${formatDate(grant.valid_until)}`
                          : ''}
                      </Text>
                    </GrantMeta>

                    {/* The difference between a manager who keeps working when
                        a new building is bought and one who silently does not. */}
                    {grant.is_open &&
                      isSelfExpandingScope(grant.scope_type) && (
                        <OrgWideBanner>
                          <BannerIcon aria-hidden="true">&#8635;</BannerIcon>
                          <Text
                            mt={0}
                            mb={0}
                            fontSize={14}
                            color={palette.slate}
                          >
                            {grant.scope_type === 'organization'
                              ? 'Covers every building in the organisation, including any building added later — no one has to update this grant.'
                              : 'Covers every building in this portfolio, including buildings moved or added into it later.'}
                          </Text>
                        </OrgWideBanner>
                      )}

                    {grant.is_open && redundant && (
                      <Text mt={0} mb={0} fontSize={12} color={palette.slate}>
                        Another grant already covers all of these buildings, so
                        revoking this one on its own would not remove access.
                      </Text>
                    )}
                  </GrantCard>
                );
              })}
              </Presence>
            </GrantList>
          )}
        </Section>

        {/* ── Resolved access ────────────────────────────────────────── */}
        <Section>
          <SectionHeader>
            <SectionTitleGroup>
              <SectionEyebrow>Step 2 — computed result</SectionEyebrow>
              <Text
                mt={0}
                mb={0}
                fontSize={18}
                fontWeight={700}
                color={palette.ink}
              >
                Resolved access
              </Text>
              <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
                The buildings those grants actually open up. Not editable —
                change a grant above and this list follows.
              </Text>
            </SectionTitleGroup>

            <CountPill tone={hasNoAccess ? 'warning' : 'brand'}>
              {resolvedBuildings.length}{' '}
              {resolvedBuildings.length === 1 ? 'building' : 'buildings'}
            </CountPill>
          </SectionHeader>

          {orgWideGrant && resolvedBuildings.length > 0 && (
            <OrgWideBanner>
              <BannerIcon aria-hidden="true">&#127758;</BannerIcon>
              <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
                A single organisation-wide grant produced{' '}
                {resolvedBuildings.length === 1
                  ? 'this building'
                  : `all ${resolvedBuildings.length} of these buildings`}
                . Any building added later joins this list automatically.
              </Text>
            </OrgWideBanner>
          )}

          {resolvedBuildings.length === 0 ? (
            <SectionEmpty>
              <Text
                mt={0}
                mb={0}
                fontSize={14}
                fontWeight={600}
                color={palette.inkSoft}
              >
                No buildings
              </Text>
              <Text mt={0} mb={0} fontSize={14} color={palette.slate}>
                {openGrants.length > 0
                  ? 'The grants above do not currently resolve to any building — the portfolio or building they point at may be empty or removed.'
                  : 'Add a grant above and the buildings it opens up will appear here.'}
              </Text>
            </SectionEmpty>
          ) : (
            <ResolvedList>
              <Presence>
              {resolvedBuildings.map((building, index) => {
                const sources = explainBuildingAccess(building, grants);
                const isUnitLimited = building.unit_ids.length > 0;

                return (
                  <ResolvedRow
                    key={building.building_id}
                    {...listItemMotion(index)}
                    whileHover={{ y: -1 }}
                  >
                    <ResolvedIdentity>
                      <Text
                        mt={0}
                        mb={0}
                        fontSize={16}
                        fontWeight={600}
                        color={palette.ink}
                      >
                        {building.building_name ||
                          `Building #${building.building_id}`}
                      </Text>
                      <Text mt={0} mb={0} fontSize={12} color={palette.slate}>
                        {building.portfolio_name || 'No portfolio'}
                        {isUnitLimited
                          ? ` · limited to ${building.unit_ids.length} ${
                              building.unit_ids.length === 1 ? 'unit' : 'units'
                            }`
                          : ' · whole building'}
                      </Text>

                      <ResolvedVia>
                        <ViaLabel>Granted by</ViaLabel>
                        {sources.length === 0 ? (
                          <ViaTag tone="neutral">Source unclear</ViaTag>
                        ) : (
                          sources.map(source => (
                            <ViaTag key={source.grantId} tone={source.tone}>
                              {source.label}
                            </ViaTag>
                          ))
                        )}
                      </ResolvedVia>
                    </ResolvedIdentity>

                    <TagRow>
                      <CapabilityTag
                        tone="success"
                        muted={!building.can_operate}
                      >
                        {building.can_operate ? '✓ ' : ''}
                        {CAPABILITY_COPY.operate.title}
                      </CapabilityTag>
                      <CapabilityTag
                        tone="warning"
                        muted={!building.can_administer}
                      >
                        {building.can_administer ? '✓ ' : ''}
                        {CAPABILITY_COPY.administer.title}
                      </CapabilityTag>
                    </TagRow>
                  </ResolvedRow>
                );
              })}
              </Presence>
            </ResolvedList>
          )}
        </Section>
      </Column>

    </Staleable>
  );
  }
};
